import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import StarRating from './StarRating';
import { ReviewSummary as ReviewSummaryType, ReviewTargetTypeValue } from '../../types/review';

const SummaryContainer = styled.div`
  background: var(--legacy-card-bg) !important;
  border: 1px solid #00d4ff !important;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 40px;
  backdrop-filter: blur(10px);
  /* Принудительно убираем любое наследование цветов */
  color: inherit !important;
`;

const SummaryTitle = styled.h2`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 1.5rem;
  color: #00d4ff !important;
  text-align: center;
  margin-bottom: 25px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const RatingOverview = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const MainRating = styled.div`
  flex: 1;
  background: rgba(26, 35, 46, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3) !important;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
`;

const RatingNumber = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 3rem;
  font-weight: 700;
  color: #00d4ff !important;
  margin-bottom: 10px;
  line-height: 1;
`;

const RatingStars = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
`;

const ReviewStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
`;

const ReviewCount = styled.div`
  color: #a0a9b8;
  font-size: 14px;
  font-weight: 500;
`;

const VerifiedCount = styled.div`
  color: #00d4ff;
  font-size: 12px;
  opacity: 0.8;
`;

const RatingDistribution = styled.div`
  flex: 2;
  background: rgba(26, 35, 46, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3) !important;
  border-radius: 8px;
  padding: 20px;
`;

const DistributionTitle = styled.h3`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 1rem;
  color: #00d4ff !important;
  margin-bottom: 20px;
  font-weight: 600;
  text-align: center;
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 12px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StarCount = styled.div`
  color: #a0a9b8;
  font-size: 14px;
  font-weight: 500;
  width: 20px;
  text-align: center;
`;

const BarContainer = styled.div`
  flex: 1;
  height: 10px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 5px;
  overflow: hidden;
  position: relative;
`;

const Bar = styled.div<{ width: number }>`
  height: 100%;
  background: linear-gradient(90deg, #00d4ff, #0099cc);
  width: ${props => props.width}%;
  transition: width 0.4s ease;
  border-radius: 5px;
`;

const CountNumber = styled.div`
  color: #00d4ff;
  font-size: 14px;
  font-weight: 600;
  width: 30px;
  text-align: right;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  padding-top: 25px;
  border-top: 1px solid rgba(0, 212, 255, 0.1) !important;
  flex-wrap: wrap;
`;

const ViewAllButton = styled.button`
  background: linear-gradient(135deg, #00d4ff, #0099cc) !important;
  border: none !important;
  border-radius: 8px;
  color: white !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    background: linear-gradient(135deg, #00a8cc, #007799) !important;
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(0, 212, 255, 0.4);
  }
`;

const WriteReviewButton = styled.button`
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(0, 212, 255, 0.3) !important;
  border-radius: 8px;
  color: #00d4ff !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    background: rgba(0, 212, 255, 0.1) !important;
    border-color: #00d4ff !important;
    transform: translateY(-2px);
  }
`;

const WriteReviewMessage = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 14px;
  color: #a0a9b8;
  text-align: center;
  font-style: italic;
  padding: 12px 20px;
`;

const NoReviewsMessage = styled.div`
  text-align: center;
  color: #a0a9b8;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 16px;
  padding: 60px 30px;
  line-height: 1.6;
`;

interface ReviewSummaryProps {
  summary: ReviewSummaryType;
  loading?: boolean;
  onViewAllReviews?: () => void; // Legacy callback - will be deprecated
  targetType?: ReviewTargetTypeValue; // New: for navigation to reviews page
  targetId?: string; // New: for navigation to reviews page
  className?: string;
  // Write Review props
  showWriteReview?: boolean;
  onWriteReview?: () => void;
  writeReviewText?: string;
  canWriteReview?: boolean;
  writeReviewMessage?: string;
}

const ReviewSummary: React.FC<ReviewSummaryProps> = ({
  summary,
  loading = false,
  onViewAllReviews,
  targetType,
  targetId,
  className,
  showWriteReview = false,
  onWriteReview,
  writeReviewText = 'Write Review',
  canWriteReview = false,
  writeReviewMessage
}) => {
  const navigate = useNavigate();

  const handleViewAllReviews = () => {
    // New navigation approach - navigate to reviews page
    if (targetType && targetId) {
      navigate(`/reviews/${targetType}/${targetId}`);
    } else if (onViewAllReviews) {
      // Legacy fallback - use callback
      onViewAllReviews();
    }
  };

  if (loading) {
    return (
      <SummaryContainer className={className}>
        <SummaryTitle>REVIEWS</SummaryTitle>
        <NoReviewsMessage>Loading reviews...</NoReviewsMessage>
      </SummaryContainer>
    );
  }

  // Удаляем раннее возвращение для пустых отзывов - показываем полный интерфейс

  const maxCount = Math.max(...Object.values(summary.ratingDistribution), 0);

  return (
    <SummaryContainer className={className}>
      <SummaryTitle>REVIEWS</SummaryTitle>
      
      <RatingOverview>
        <MainRating>
          <RatingNumber>{summary.rating.toFixed(1)}</RatingNumber>
          <RatingStars>
            <StarRating 
              rating={summary.rating} 
              readOnly 
              size="large" 
            />
          </RatingStars>
          <ReviewStats>
            <ReviewCount>
              {summary.reviewCount} review{summary.reviewCount !== 1 ? 's' : ''}
            </ReviewCount>
            {summary.reviewCountConfirmed > 0 && (
              <VerifiedCount>
                {summary.reviewCountConfirmed} verified
              </VerifiedCount>
            )}
          </ReviewStats>
        </MainRating>

        <RatingDistribution>
          <DistributionTitle>Rating Distribution</DistributionTitle>
          {[5, 4, 3, 2, 1].map(star => {
            const count = summary.ratingDistribution[star] || 0;
            const percentage = summary.reviewCount > 0 ? (count / summary.reviewCount) * 100 : 0;
            
            return (
              <RatingRow key={star}>
                <StarCount>{star}</StarCount>
                <div style={{ color: '#a0a9b8', fontSize: '14px' }}>★</div>
                <BarContainer>
                  <Bar width={percentage} />
                </BarContainer>
                <CountNumber>{count}</CountNumber>
              </RatingRow>
            );
          })}
        </RatingDistribution>
      </RatingOverview>

      {((targetType && targetId) || onViewAllReviews || showWriteReview) && (
        <ActionBar>
          {(targetType && targetId) || onViewAllReviews ? (
            <ViewAllButton onClick={handleViewAllReviews}>
              View All Reviews
            </ViewAllButton>
          ) : null}
          
          {showWriteReview && (
            <>
              {canWriteReview ? (
                <WriteReviewButton onClick={onWriteReview}>
                  {writeReviewText}
                </WriteReviewButton>
              ) : (
                writeReviewMessage && (
                  <WriteReviewMessage>
                    {writeReviewMessage}
                  </WriteReviewMessage>
                )
              )}
            </>
          )}
        </ActionBar>
      )}
    </SummaryContainer>
  );
};

export default ReviewSummary;